import { Check, SkipForward, X } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { ImportRowPreview, ImportRowStatus } from "@/api/types";

interface ImportPreviewTableProps {
  rows: ImportRowPreview[];
}

const STATUS_VISUAL: Record<
  ImportRowStatus,
  {
    icon: typeof Check;
    rowClass: string;
    iconClass: string;
    label: string;
  }
> = {
  valid: {
    icon: Check,
    rowClass: "bg-white",
    iconClass: "text-emerald-500",
    label: "Valid",
  },
  invalid: {
    icon: X,
    rowClass: "bg-rose-50/40",
    iconClass: "text-rose-500",
    label: "Invalid",
  },
  duplicate: {
    icon: SkipForward,
    rowClass: "bg-amber-50/40",
    iconClass: "text-amber-500",
    label: "Duplicate",
  },
};

export function ImportPreviewTable({ rows }: ImportPreviewTableProps) {
  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-200 px-6 py-10 text-center text-sm text-slate-400">
        No rows to preview
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-slate-900/[0.06]">
      <div className="max-h-[360px] overflow-y-auto">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-slate-50">
            <TableRow className="border-slate-900/[0.06] hover:bg-transparent">
              <TableHead className="w-12 text-[11px] font-medium uppercase tracking-wider text-slate-500">
                #
              </TableHead>
              <TableHead className="w-12 text-[11px] font-medium uppercase tracking-wider text-slate-500">
                <span className="sr-only">Status</span>
              </TableHead>
              <TableHead className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                Name
              </TableHead>
              <TableHead className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                Domain
              </TableHead>
              <TableHead className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                Contact
              </TableHead>
              <TableHead className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                Sector
              </TableHead>
              <TableHead className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                Service
              </TableHead>
              <TableHead className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                Reason
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const visual = STATUS_VISUAL[row.status];
              const Icon = visual.icon;
              const data = row.data as Record<string, unknown>;

              return (
                <TableRow
                  key={row.row}
                  className={cn(
                    "border-slate-900/[0.06] text-sm",
                    visual.rowClass,
                  )}
                >
                  <TableCell className="text-xs tabular-nums text-slate-400">
                    {row.row}
                  </TableCell>
                  <TableCell>
                    <span
                      className={cn(
                        "inline-flex h-5 w-5 items-center justify-center rounded-full",
                        row.status === "valid" && "bg-emerald-50",
                        row.status === "invalid" && "bg-rose-50",
                        row.status === "duplicate" && "bg-amber-50",
                      )}
                      title={visual.label}
                    >
                      <Icon className={cn("h-3 w-3", visual.iconClass)} />
                    </span>
                  </TableCell>
                  <TableCell className="font-medium text-slate-900">
                    {asString(data.name) ?? (
                      <span className="text-slate-400">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-500">
                    {asString(data.domain) ?? "—"}
                  </TableCell>
                  <TableCell className="text-slate-500">
                    {asString(data.contact_name) ?? "—"}
                  </TableCell>
                  <TableCell className="text-slate-500">
                    {asString(data.sector) ?? "—"}
                  </TableCell>
                  <TableCell className="text-slate-500">
                    {asString(data.service) ?? "—"}
                  </TableCell>
                  <TableCell
                    className={cn(
                      "max-w-[260px] truncate text-xs",
                      row.status === "invalid" && "text-rose-600",
                      row.status === "duplicate" && "text-amber-700",
                      row.status === "valid" && "text-slate-400",
                    )}
                    title={row.reason ?? undefined}
                  >
                    {row.reason ?? ""}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function asString(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const trimmed = v.trim();
  return trimmed.length > 0 ? trimmed : null;
}
